import { ref } from "vue";

// История поиска («недавнее»): запросы и открытые объекты - удобство
// конкретного браузера (localStorage), без бэкенда. Состояние общее на модуль:
// поле есть и в десктопной панели, и в мобильной шторке, история у них одна.
const STORAGE_KEY = "eco_search_history";
const MAX_ITEMS = 20;

export type HistoryObjectKind = "route" | "place" | "poi";
export type HistoryEntry =
  | { type: "query"; text: string; at: number }
  | {
      type: "object";
      kind: HistoryObjectKind;
      id: string;
      name: string;
      subtitle: string;
      // null - у объекта нет геометрии (маршрут без линии и старта): не выдумываем (0, 0).
      lat: number | null;
      lon: number | null;
      routeId?: number;
      osmKind?: string; // точки: сырой kind бэкенда - для иконки в «Недавних»
      at: number;
    };

export type HistoryObjectInput = Omit<Extract<HistoryEntry, { type: "object" }>, "type" | "at">;

const KINDS: HistoryObjectKind[] = ["route", "place", "poi"];

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null;

const load = (): HistoryEntry[] => {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    if (!Array.isArray(parsed)) return [];
    const base = Date.now();
    const out: HistoryEntry[] = [];
    parsed.forEach((raw, i) => {
      // Старый формат - строки; порядок сохраняем убывающими метками времени.
      if (typeof raw === "string") {
        out.push({ type: "query", text: raw, at: base - i });
        return;
      }
      if (!isObj(raw) || typeof raw.at !== "number") return;
      if (raw.type === "query" && typeof raw.text === "string") {
        out.push({ type: "query", text: raw.text, at: raw.at });
      } else if (
        raw.type === "object" &&
        KINDS.includes(raw.kind as HistoryObjectKind) &&
        typeof raw.id === "string" &&
        typeof raw.name === "string" &&
        typeof raw.subtitle === "string" &&
        (typeof raw.lat === "number" || raw.lat === null) &&
        (typeof raw.lon === "number" || raw.lon === null)
      ) {
        out.push({
          type: "object",
          kind: raw.kind as HistoryObjectKind,
          id: raw.id,
          name: raw.name,
          subtitle: raw.subtitle,
          lat: raw.lat as number | null,
          lon: raw.lon as number | null,
          ...(typeof raw.routeId === "number" ? { routeId: raw.routeId } : {}),
          ...(typeof raw.osmKind === "string" ? { osmKind: raw.osmKind } : {}),
          at: raw.at,
        });
      }
    });
    return out.slice(0, MAX_ITEMS);
  } catch {
    return [];
  }
};

const items = ref<HistoryEntry[]>(load());

const save = () => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items.value));
  } catch {
    /* хранилище недоступно - история просто не переживёт перезагрузку */
  }
};

// Метка строго возрастает, даже если две записи пришли в одну миллисекунду.
let lastAt = 0;
const nextAt = () => (lastAt = Math.max(Date.now(), lastAt + 1));

export function useSearchHistory() {
  // Запоминаем осмысленный запрос (от 2 символов), без дублей без учёта регистра;
  // свежий - первым.
  const addQuery = (text: string) => {
    const q = text.trim();
    if (q.length < 2) return;
    const rest = items.value.filter((e) => !(e.type === "query" && e.text.toLowerCase() === q.toLowerCase()));
    items.value = [{ type: "query" as const, text: q, at: nextAt() }, ...rest].slice(0, MAX_ITEMS);
    save();
  };
  // Открытый объект; повтор (тот же kind+id) поднимается наверх с новыми данными.
  const addObject = (e: HistoryObjectInput) => {
    const rest = items.value.filter((x) => !(x.type === "object" && x.kind === e.kind && x.id === e.id));
    items.value = [{ ...e, type: "object" as const, at: nextAt() }, ...rest].slice(0, MAX_ITEMS);
    save();
  };
  const clear = () => {
    items.value = [];
    save();
  };
  return { items, addQuery, addObject, clear };
}
